import type { Note } from '@/types/note';
import { noteArraySchema } from '@/validation/noteSchema';

const CURRENT_VERSION = 1;
const PRIMARY_KEY = `notes:v${CURRENT_VERSION}`;
const QUARANTINE_KEY = `notes:corrupt:v${CURRENT_VERSION}`;
const LEGACY_KEYS = ['notes:v0'] as const;

export interface PersistedShape {
  readonly version: number;
  readonly notes: readonly Note[];
  readonly savedAt: number;
}

export interface LoadOutcome {
  readonly notes: Note[];
  readonly quarantined: boolean;
  readonly reason?: string;
}

/**
 * Versioned localStorage adapter. Reads apply schema validation before
 * returning; anything that fails is quarantined under a `notes:corrupt:*` key
 * for debugging rather than silently dropped.
 *
 * The migration chain is a `switch` on stored version → runs migration →
 * writes back at CURRENT_VERSION.
 */
export class PersistentStore {
  constructor(private readonly storage: Storage = safeLocalStorage()) {}

  load(): LoadOutcome {
    for (const legacy of LEGACY_KEYS) {
      const migrated = this.tryMigrateFrom(legacy);
      if (migrated) return migrated;
    }

    const raw = this.storage.getItem(PRIMARY_KEY);
    if (!raw) return { notes: [], quarantined: false };

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      this.quarantine(raw, 'invalid_json');
      return { notes: [], quarantined: true, reason: 'invalid_json' };
    }

    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      (parsed as PersistedShape).version !== CURRENT_VERSION
    ) {
      this.quarantine(raw, 'version_mismatch');
      return { notes: [], quarantined: true, reason: 'version_mismatch' };
    }

    const validated = noteArraySchema((parsed as PersistedShape).notes ?? []);
    if (!validated.ok) {
      this.quarantine(raw, 'schema_invalid');
      return { notes: [], quarantined: true, reason: 'schema_invalid' };
    }

    return { notes: validated.value, quarantined: false };
  }

  save(notes: readonly Note[]): void {
    const payload: PersistedShape = {
      version: CURRENT_VERSION,
      notes,
      savedAt: Date.now(),
    };
    try {
      this.storage.setItem(PRIMARY_KEY, JSON.stringify(payload));
    } catch (e) {
      // Quota exceeded or storage disabled — surface via console; UI decides.
      console.warn('PersistentStore.save failed', e);
    }
  }

  clear(): void {
    this.storage.removeItem(PRIMARY_KEY);
  }

  private quarantine(raw: string, reason: string): void {
    try {
      this.storage.setItem(QUARANTINE_KEY, JSON.stringify({ raw, reason, at: Date.now() }));
      this.storage.removeItem(PRIMARY_KEY);
    } catch {
      // best-effort
    }
  }

  /**
   * Migration seam: today no-op (no prior versions exist), but the shape is here
   * so a future v0 → v1 change is a small edit rather than a rewrite.
   */
  private tryMigrateFrom(legacyKey: string): LoadOutcome | null {
    const raw = this.storage.getItem(legacyKey);
    if (!raw) return null;
    // Placeholder migration path — for now, quarantine and reset.
    this.quarantine(raw, `legacy:${legacyKey}`);
    return { notes: [], quarantined: true, reason: `legacy:${legacyKey}` };
  }
}

function safeLocalStorage(): Storage {
  try {
    return globalThis.localStorage;
  } catch {
    return memoryStorage();
  }
}

/** No-op storage for SSR / disabled localStorage. */
function memoryStorage(): Storage {
  const map = new Map<string, string>();
  return {
    get length() {
      return map.size;
    },
    key: (i: number) => [...map.keys()][i] ?? null,
    getItem: (k) => (map.has(k) ? (map.get(k) ?? null) : null),
    setItem: (k, v) => {
      map.set(k, v);
    },
    removeItem: (k) => {
      map.delete(k);
    },
    clear: () => {
      map.clear();
    },
  };
}
